import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import debounce from 'lodash/debounce';
import { observer } from 'mobx-react';
// ui-library
import { DoubleArrowLeftIcon, DoubleArrowRightIcon, View, Text } from '@upstox/upstox-ui';
// components
import OptionChainTable from 'modules/OptionChain/components/OptionChainModal/components/OptionChainTableContainer/OptionChainTable';
import StrikesTable from 'modules/OptionChain/components/OptionChainModal/components/OptionChainTableContainer/StrikesTable/StrikesTable';
// hooks
import { useSubscribeScripAndGetValue } from 'utils/hooks';
// stores
import { useStore } from 'modules/Stores';
// constants
import { MARKET_FEEDER_MODES } from 'modules/LiveData/constants';
// utils
import { getHeaderColumns } from './OptionChainTable/utils';
// styles
import * as css from './styles';

const OptionChainTableContainer: FC = () => {
  // table refs
  const putTableRef = useRef<HTMLDivElement | null>(null);
  const callTableRef = useRef<HTMLDivElement | null>(null);
  const strikesTableRef = useRef<HTMLDivElement | null>(null);

  // scroll refs
  const isCallScrolling = useRef(false);
  const isPutScrolling = useRef(false);
  const isWheelScrollActive = useRef(false);
  const previousPutTableScroll = useRef(0);
  const isFocussingOnSpotRow = useRef(false);

  const { spotScripData, loading, activeStrikes, tableSettings } = useStore('OptionChainStore');
  const headerColumns = getHeaderColumns(tableSettings, 'CE');
  const totalNoOfColumns = headerColumns.length;

  const stopCallScrolling = useCallback(
    debounce(() => {
      isCallScrolling.current = false;
    }, 100),
    [],
  );

  const stopPutScrolling = useCallback(
    debounce(() => {
      isPutScrolling.current = false;
    }, 100),
    [],
  );

  const stopWheelScroll = useCallback(
    debounce(() => {
      isWheelScrollActive.current = false;
    }, 100),
    [],
  );

  const syncVerticalScroll = (scrollTop: number) => {
    const callTable = callTableRef.current as HTMLDivElement;
    const strikesTable = strikesTableRef.current as HTMLDivElement;
    const putTable = putTableRef.current as HTMLDivElement;

    if (callTable) {
      callTable.scrollTop = scrollTop;
    }

    if (putTable) {
      putTable.scrollTop = scrollTop;
    }

    if (strikesTable) {
      strikesTable.scrollTop = scrollTop;
    }
  };

  const handleCallScroll = useCallback(() => {
    // return if
    // 1. spot row is focused
    // 2. wheel scroll is active
    // 3. put table is scrolling
    if (
      isPutScrolling.current ||
      isWheelScrollActive.current ||
      isFocussingOnSpotRow.current
    ) return;

    const callTable = callTableRef.current as HTMLDivElement;
    const putTable = putTableRef.current as HTMLDivElement;

    if (callTable && putTable) {
      isCallScrolling.current = true;

      // horizontal scroll the put table to the right
      requestAnimationFrame(() => {
        putTable.scrollLeft =
          callTable.scrollWidth -
          callTable.clientWidth -
          callTable.scrollLeft;

        stopCallScrolling();
      });
    }
  }, [stopCallScrolling]);

  const handlePutScroll = useCallback(() => {
    // return if
    // 1. spot row is focused
    // 2. wheel scroll is active
    // 3. call table is scrolling
    if (
      isFocussingOnSpotRow.current ||
      isWheelScrollActive.current ||
      isCallScrolling.current
    ) return;

    const callTable = callTableRef.current as HTMLDivElement;
    const strikesTable = strikesTableRef.current as HTMLDivElement;
    const putTable = putTableRef.current as HTMLDivElement;

    if (callTable && strikesTable && putTable) {
      isPutScrolling.current = true;

      requestAnimationFrame(() => {
        const currentPutScrollTop = putTable.scrollTop;
        if (
          Math.abs(currentPutScrollTop - previousPutTableScroll.current) > 0.5
        ) {
          callTable.scrollTop = currentPutScrollTop;
          strikesTable.scrollTop = currentPutScrollTop;
          previousPutTableScroll.current = currentPutScrollTop;
        } else {
          // horizontal scroll the call table to the left
          callTable.scrollLeft =
            putTable.scrollWidth -
            putTable.clientWidth -
            putTable.scrollLeft;
        }

        stopPutScrolling();
      });
    }
  }, [stopPutScrolling]);

  const handleCallTableWheel = useCallback(
    (event: WheelEvent) => {
      const { deltaX, deltaY } = event;

      if (deltaY !== 0) {
        event.preventDefault();
      }

      const callTable = callTableRef.current as HTMLDivElement;
      const putTable = putTableRef.current as HTMLDivElement;

      if (callTable && putTable) {
        requestAnimationFrame(() => {
          isWheelScrollActive.current = true;
          // vertical scroll sync
          const updatedScroll = putTable?.scrollTop + deltaY;
          syncVerticalScroll(updatedScroll);
          // horizontal scroll sync
          putTable.scrollLeft =
            putTable.scrollWidth -
            callTable.clientWidth -
            callTable?.scrollLeft -
            deltaX;
          stopWheelScroll();
        });
      }
    },
    [stopWheelScroll],
  );

  const handlePutTableWheel = useCallback(
    (event: WheelEvent) => {
      const { deltaX, deltaY } = event;

      if (deltaY !== 0) {
        event.preventDefault();
      }

      const callTable = callTableRef.current as HTMLDivElement;
      const putTable = putTableRef.current as HTMLDivElement;

      if (callTable && putTable) {
        requestAnimationFrame(() => {
          isWheelScrollActive.current = true;
          // vertical scroll sync
          const updatedScroll = callTable?.scrollTop + deltaY;
          syncVerticalScroll(updatedScroll);
          // horizontal scroll sync
          callTable.scrollLeft =
            callTable.scrollWidth -
            putTable?.clientWidth -
            putTable?.scrollLeft -
            deltaX;

          stopWheelScroll();
        });
      }
    },
    [stopWheelScroll],
  );

  const handleStrikesTableWheel = useCallback(
    (event: WheelEvent) => {
      const { deltaY } = event;

      if (deltaY !== 0) {
        event.preventDefault();
      }

      const strikesTable = strikesTableRef.current as HTMLDivElement;

      if (strikesTable) {
        requestAnimationFrame(() => {
          isWheelScrollActive.current = true;
          // vertical scroll sync
          const updatedScroll = strikesTable.scrollTop + deltaY;
          syncVerticalScroll(updatedScroll);
          stopWheelScroll();
        });
      }
    },
    [stopWheelScroll],
  );

  // Function to focus to the spot price row and scroll call table to the right
  const focusOnSpotRow = useCallback(() => {
    const spotRow = document.getElementById('spot-row');
    const callTable = callTableRef.current as HTMLDivElement;
    const putTable = putTableRef.current as HTMLDivElement;
    const strikesTable = strikesTableRef.current as HTMLDivElement;

    if (!spotRow || !callTable || !putTable || !strikesTable) {
      return;
    }

    isFocussingOnSpotRow.current = true;

    const rowPosition = spotRow.offsetTop;
    const strikesPaddingBottom = putTable.offsetHeight - putTable.clientHeight;

    // Define scroll targets for vertical and horizontal scrolling
    const scrollTargets = [
      {
        table: callTable,
        targetTop: rowPosition - callTable.clientHeight / 2,
        targetLeft: callTable.scrollWidth, // Scroll to the right
      },
      {
        table: putTable,
        targetTop: rowPosition - putTable.clientHeight / 2,
        targetLeft: 0, // Scroll to the left
      },
      {
        table: strikesTable,
        targetTop: rowPosition - strikesTable.clientHeight / 2 +
          strikesPaddingBottom / 2,
        targetLeft: 0, // No horizontal scroll needed for strikesTable
      },
    ];

    const animationFrameId = requestAnimationFrame(() => {
      scrollTargets.forEach(({ table, targetTop, targetLeft }) => {
        // eslint-disable-next-line no-param-reassign
        table.scrollTop = targetTop;
        // eslint-disable-next-line no-param-reassign
        table.scrollLeft = targetLeft;
      });
      isFocussingOnSpotRow.current = false;
    });

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      isFocussingOnSpotRow.current = false;
    };
  }, []);

  // Adds scrollbar padding to strikes table
  const setScrollbarPadding = useCallback(() => {
    const callTable = callTableRef.current as HTMLDivElement;
    const putTable = putTableRef.current as HTMLDivElement;

    if (callTable && putTable && strikesTableRef?.current?.style) {
      const scrollbarWidth = putTable.offsetHeight - putTable.clientHeight;
      strikesTableRef.current.style.paddingBottom = `${scrollbarWidth}px`;
    }
  }, []);

  const getCePeLabel = (isCall: boolean) => {
    const ArrowIcon = isCall ? DoubleArrowLeftIcon : DoubleArrowRightIcon;
    const type = isCall ? 'CE' : 'PE';
    return (
      <View css={css.callPutLabelWrapper} cssProps={{ isCall }}>
        <View css={css.callPutLabelContainer}>
          <View
            alignItems="center"
            css={css.callPutLabelContent}
            cssProps={{ isCall }}
          >
            <ArrowIcon color="interactive.primary.default" size="small" />
            <Text css={css.callPutLabel}>{type}</Text>
          </View>
        </View>
      </View>
    );
  };

  // subscribe to spot scrip live data
  useSubscribeScripAndGetValue(
    // getSpotScripInstrumentKey(spotScripData),
    spotScripData?.instrumentKey || '',
    MARKET_FEEDER_MODES.LTPC,
    true,
  );

  useEffect(() => {
    const callTable = callTableRef.current as HTMLDivElement;
    const putTable = putTableRef.current as HTMLDivElement;
    const strikesTable = strikesTableRef.current as HTMLDivElement;

    if (callTable) {
      callTable.addEventListener('wheel', handleCallTableWheel, {
        passive: false,
      });
    }

    if (putTable) {
      putTable.addEventListener('wheel', handlePutTableWheel, {
        passive: false,
      });
    }

    if (strikesTable) {
      strikesTable.addEventListener('wheel', handleStrikesTableWheel, {
        passive: false,
      });
    }

    return () => {
      if (callTable) {
        callTable.removeEventListener('wheel', handleCallTableWheel);
      }

      if (putTable) {
        putTable.removeEventListener('wheel', handlePutTableWheel);
      }

      if (strikesTable) {
        strikesTable.removeEventListener('wheel', handleStrikesTableWheel);
      }
    };
  }, [handleCallTableWheel, handlePutTableWheel, handleStrikesTableWheel]);

  // scroll to the center of the tables
  useLayoutEffect(() => {
    if (loading) return;
    setScrollbarPadding();

    let cleanup: (() => void) | undefined;
    // Ensure tables are fully rendered before triggering focusOnSpotRow
    const timeoutId = setTimeout(() => {
      cleanup = focusOnSpotRow();
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      if (cleanup) cleanup();
    };
  }, [
    loading,
    focusOnSpotRow,
    setScrollbarPadding,
    activeStrikes.length,
    totalNoOfColumns,
  ]);

  return (
    <View flex css={css.optionChainTableContainer}>
      <View css={css.callPutContainer}>
        {getCePeLabel(true)}
        <View
          ref={callTableRef}
          css={css.tableContainer}
          onScroll={handleCallScroll}
          className="hidden-scrollbar"
        >
          <View css={css.optionChainTableWrapper}>
            <OptionChainTable
              type="CE"
              dataId="leftTableOCRow"
            />
          </View>
        </View>
      </View>
      <View
        ref={strikesTableRef}
        css={css.strikesTableContainer}
        className="hidden-scrollbar"
      >
        <StrikesTable />
      </View>
      <View css={css.callPutContainer}>
        {getCePeLabel(false)}
        <View
          ref={putTableRef}
          css={css.tableContainer}
          onScroll={handlePutScroll}
        >
          <View css={css.optionChainTableWrapper}>
            <OptionChainTable
              type="PE"
              dataId="rightTableOCRow"
            />
          </View>
        </View>
      </View>
    </View>
  );
};

export default observer(OptionChainTableContainer);
